import { useEffect, useState, ReactElement } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, Timestamp, collection, getDocs, arrayUnion } from "firebase/firestore";
import { onAuthStateChanged, User, signOut } from "firebase/auth";
import { api } from "../services/api";
import { workspaceNameToSlug } from "../utils/slug";
import { ensureUserDocumentExists, validateUserDocumentExists } from "../utils/memberUtils";
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import LoginPage from "./LoginPage";
import logo from "../assets/BudgetTrace Logomark_V2.svg";

interface InviteAcceptanceProps {
  invitationId: string;
  onAccept?: (workspaceId: string) => void;
}

function InviteAcceptance({ invitationId, onAccept }: InviteAcceptanceProps): ReactElement {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<any>(null);
  const [workspace, setWorkspace] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [accepted, setAccepted] = useState<boolean>(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Prevent duplicate runs in development (React StrictMode)
    let isMounted = true;
    
    if (!invitationId) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }

    const fetchInvitation = async (): Promise<void> => {
      try {
        // Find the invitation by searching all workspaces
        // Since we allow public read of invitations, we can try to find it
        // First, try to get workspaces if user has access
        let foundInvitation: {
          id: string;
          email: string;
          workspaceId: string;
          invitedBy: string;
          status: "pending" | "accepted" | "expired" | "declined";
          role?: "admin" | "user" | "viewer";
          createdAt: any;
          expiresAt: any;
        } | null = null;
        let foundWorkspace: any = null;
        
        try {
          // Try to query workspaces if user has access
          const workspacesRef = collection(db, "workspaces");
          const workspacesSnapshot = await getDocs(workspacesRef);
          
          for (const workspaceDoc of workspacesSnapshot.docs) {
            try {
              const invitationRef = doc(db, "workspaces", workspaceDoc.id, "invitations", invitationId);
              const invitationSnap = await getDoc(invitationRef);
              
              if (invitationSnap.exists()) {
                const invitationData = invitationSnap.data();
                foundInvitation = { 
                  id: invitationSnap.id, 
                  email: invitationData.email as string,
                  workspaceId: invitationData.workspaceId as string,
                  invitedBy: invitationData.invitedBy as string,
                  status: invitationData.status as "pending" | "accepted" | "expired" | "declined",
                  role: invitationData.role as "admin" | "user" | "viewer" | undefined,
                  createdAt: invitationData.createdAt,
                  expiresAt: invitationData.expiresAt,
                };
                foundWorkspace = { id: workspaceDoc.id, ...workspaceDoc.data() };
                break;
              }
            } catch (err) {
              // Permission denied for this workspace's invitations, continue searching
              continue;
            }
          }
        } catch (err) {
          // If user can't query workspaces (not authenticated or no access),
          // we need to use a backend endpoint or collectionGroup query
          // For now, try to find it by checking if we can get user's email from auth
          // and then check invitations by email using collectionGroup
        }
        
        // If not found via workspace query, try using backend API endpoint
        // This works for both authenticated and unauthenticated users
        if (!foundInvitation) {
          try {
            // Add timeout to prevent infinite hanging
            const apiCallPromise = api.getInvitation(invitationId);
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error("API request timeout after 30 seconds")), 30000);
            });
            
            const invitationData = await Promise.race([apiCallPromise, timeoutPromise]) as any;
            
            // Validate response
            if (!invitationData || !invitationData.id || !invitationData.workspaceId) {
              console.error("Invalid invitation data received:", invitationData);
              setError("Invalid invitation data received from server.");
              setLoading(false);
              return;
            }
            
            // Convert timestamps properly - backend sends Firestore Timestamps which are serialized
            // They come as objects with _seconds and _nanoseconds or as ISO strings
            let createdAt: Timestamp | null = null;
            let expiresAt: Timestamp | null = null;
            
            if (invitationData.createdAt) {
              try {
                // Check if it's a Firestore Timestamp object (from backend)
                if (invitationData.createdAt._seconds !== undefined) {
                  createdAt = Timestamp.fromMillis(invitationData.createdAt._seconds * 1000 + (invitationData.createdAt._nanoseconds || 0) / 1000000);
                } else if (invitationData.createdAt.seconds !== undefined) {
                  createdAt = Timestamp.fromMillis(invitationData.createdAt.seconds * 1000 + (invitationData.createdAt.nanoseconds || 0) / 1000000);
                } else if (typeof invitationData.createdAt === 'string') {
                  createdAt = Timestamp.fromDate(new Date(invitationData.createdAt));
                } else if (invitationData.createdAt instanceof Timestamp) {
                  createdAt = invitationData.createdAt;
                } else {
                  createdAt = Timestamp.fromDate(new Date(invitationData.createdAt));
                }
              } catch (e) {
                console.warn("Error parsing createdAt:", e);
                createdAt = Timestamp.now();
              }
            }
            
            if (invitationData.expiresAt) {
              try {
                // Check if it's a Firestore Timestamp object (from backend)
                if (invitationData.expiresAt._seconds !== undefined) {
                  expiresAt = Timestamp.fromMillis(invitationData.expiresAt._seconds * 1000 + (invitationData.expiresAt._nanoseconds || 0) / 1000000);
                } else if (invitationData.expiresAt.seconds !== undefined) {
                  expiresAt = Timestamp.fromMillis(invitationData.expiresAt.seconds * 1000 + (invitationData.expiresAt.nanoseconds || 0) / 1000000);
                } else if (typeof invitationData.expiresAt === 'string') {
                  expiresAt = Timestamp.fromDate(new Date(invitationData.expiresAt));
                } else if (invitationData.expiresAt instanceof Timestamp) {
                  expiresAt = invitationData.expiresAt;
                } else {
                  expiresAt = Timestamp.fromDate(new Date(invitationData.expiresAt));
                }
              } catch (e) {
                console.warn("Error parsing expiresAt:", e);
                // Set to 7 days from now if parsing fails
                const future = new Date();
                future.setDate(future.getDate() + 7);
                expiresAt = Timestamp.fromDate(future);
              }
            }
            
            foundInvitation = {
              id: invitationData.id,
              email: invitationData.email,
              workspaceId: invitationData.workspaceId,
              invitedBy: invitationData.invitedBy,
              status: invitationData.status,
              role: invitationData.role,
              createdAt: createdAt || Timestamp.now(),
              expiresAt: expiresAt || (() => {
                const future = new Date();
                future.setDate(future.getDate() + 7);
                return Timestamp.fromDate(future);
              })(),
            };
            
            // Use workspace data from API response (since user might not have access to read workspace)
            // Always create workspace object if we have workspaceId
            if (invitationData.workspaceId) {
              foundWorkspace = {
                id: invitationData.workspaceId,
                name: invitationData.workspaceName || "Workspace",
                logoUrl: invitationData.workspaceLogoUrl,
              };
            } else if (foundInvitation.workspaceId) {
              // Fallback: use workspaceId from invitation
              foundWorkspace = {
                id: foundInvitation.workspaceId,
                name: "Workspace",
              };
            }
            // Ensure workspace is set if invitation is set
            if (foundInvitation && !foundWorkspace && foundInvitation.workspaceId) {
              foundWorkspace = {
                id: foundInvitation.workspaceId,
                name: "Workspace",
              };
            }
          } catch (err) {
            console.error("Error fetching invitation from API:", err);
            // Set error so user sees something instead of infinite loading
            setError(err instanceof Error ? err.message : "Failed to load invitation. Please try again.");
            setLoading(false);
            return;
          }
        }

        if (!foundInvitation) {
          setError("Invitation not found or has expired");
          setLoading(false);
          return;
        }
        
        // Final check: ensure workspace is set if we have invitation
        if (foundInvitation && !foundWorkspace && foundInvitation.workspaceId) {
          foundWorkspace = {
            id: foundInvitation.workspaceId,
            name: "Workspace",
          };
        }

        // Check if invitation is expired
        const expiresAt = foundInvitation.expiresAt;
        if (expiresAt) {
          const expiresDate = expiresAt instanceof Timestamp 
            ? expiresAt.toDate() 
            : new Date(expiresAt);
          
          if (expiresDate < new Date()) {
            setError("This invitation has expired");
            setLoading(false);
            return;
          }
        }

        // Check if invitation is already accepted or declined
        if (foundInvitation.status !== "pending") {
          if (foundInvitation.status === "accepted") {
            setError("This invitation has already been accepted");
          } else if (foundInvitation.status === "declined") {
            setError("This invitation has been declined");
          } else {
            setError("This invitation is no longer valid");
          }
          setLoading(false);
          return;
        }

        // Set states
        setInvitation(foundInvitation);
        setWorkspace(foundWorkspace);
        
        // Force a small delay to ensure state updates propagate
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Only update state if component is still mounted
        if (!isMounted) {
          return;
        }
        
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        console.error("Error fetching invitation:", err);
        setError("Failed to load invitation. Please try again.");
        setLoading(false);
      }
    };

    fetchInvitation();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [invitationId]);

  const handleDeclineInvitation = async (): Promise<void> => {
    if (!invitation) {
      return;
    }
    
    // Use workspaceId from invitation if workspace object is not available
    const workspaceId = workspace?.id || invitation.workspaceId;
    if (!workspaceId) {
      setError("Workspace information is missing. Cannot decline invitation.");
      return;
    }

    setAccepting(true);
    try {
      // Update invitation status to declined
      await updateDoc(doc(db, "workspaces", workspaceId, "invitations", invitation.id), {
        status: "declined",
      });

      setAccepted(false); // Mark as declined
      setError("Invitation declined. You will be redirected shortly.");
      
      // Redirect after a short delay
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      console.error("Error declining invitation:", err);
      setError("Failed to decline invitation. Please try again.");
      setAccepting(false);
    }
  };

  const handleAcceptInvitation = async (): Promise<void> => {
    if (!user || !invitation) {
      return;
    }
    
    // Use workspaceId from invitation if workspace object is not available
    const workspaceId = workspace?.id || invitation.workspaceId;
    if (!workspaceId) {
      setError("Workspace information is missing. Cannot accept invitation.");
      setAccepting(false);
      return;
    }

    setAccepting(true);
    try {
      const userId = user.uid;
      const userEmail = user.email?.toLowerCase().trim();

      // Verify that the user's email matches the invitation email
      if (userEmail !== invitation.email.toLowerCase().trim()) {
        setError(
          `This invitation was sent to ${invitation.email}. Please sign in with that email address to accept the invitation.`
        );
        setAccepting(false);
        return;
      }

      // Ensure user document exists in users collection (create if doesn't exist)
      // This prevents orphaned members from being created
      await ensureUserDocumentExists(user);
      
      // Validate user document exists before creating member
      const userDocumentValid = await validateUserDocumentExists(userId);
      if (!userDocumentValid) {
        setError("Failed to create user account. Please try again.");
        setAccepting(false);
        return;
      }

      // Check if user is already a member
      const memberRef = doc(db, "workspaces", workspaceId, "members", userId);
      const memberSnap = await getDoc(memberRef);

      if (memberSnap.exists()) {
        // User is already a member, just update invitation status
        await updateDoc(doc(db, "workspaces", workspaceId, "invitations", invitation.id), {
          status: "accepted",
        });
        setAccepted(true);
        // Call onAccept callback or redirect
        if (onAccept) {
          setTimeout(() => {
            onAccept(workspaceId);
          }, 1500);
        } else {
          // Fallback: redirect to home
          setTimeout(() => {
            window.location.href = "/";
          }, 2000);
        }
        return;
      }

      // Get the role from invitation (default to "user" if not specified)
      const memberRole = (invitation.role && ["admin", "user", "viewer"].includes(invitation.role)) 
        ? invitation.role 
        : "user";
      
      // Add user to workspace members
      await setDoc(memberRef, {
        uid: userId,
        displayName: user.displayName || null,
        email: user.email || null,
        role: memberRole,
        joinedAt: serverTimestamp(),
      });

      // Update invitation status
      await updateDoc(doc(db, "workspaces", workspaceId, "invitations", invitation.id), {
        status: "accepted",
      });
      
      // Update user's active workspace and add to workspaceIds array
      try {
        await updateDoc(doc(db, "users", userId), {
          activeWorkspaceId: workspaceId,
          workspaceIds: arrayUnion(workspaceId),
        });
      } catch (err) {
        console.error("Error updating user's active workspace:", err);
        // Continue anyway - member document was created successfully
      }

      setAccepted(true);
      
      // Wait a moment to ensure all writes are committed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Call onAccept callback or redirect
      if (onAccept) {
        // Small delay to show success message
        setTimeout(() => {
          onAccept(workspaceId);
        }, 1000);
      } else {
        // Fallback: redirect directly to workspace
        try {
          const workspaceDoc = await getDoc(doc(db, "workspaces", workspaceId));
          if (workspaceDoc.exists()) {
            const workspaceData = workspaceDoc.data();
            const workspaceName = workspaceData?.name || "workspace";
            const slug = workspaceNameToSlug(workspaceName);
            setTimeout(() => {
              window.location.href = `/${slug}/activity`;
            }, 1000);
          } else {
            setTimeout(() => {
              window.location.href = "/";
            }, 1000);
          }
        } catch (err) {
          console.error("Error getting workspace for redirect:", err);
          setTimeout(() => {
            window.location.href = "/";
          }, 1000);
        }
      }
    } catch (err) {
      console.error("Error accepting invitation:", err);
      setError("Failed to accept invitation. Please try again.");
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="background.default"
      >
        <Paper
          elevation={0}
          sx={{ width: 360, p: 4, borderRadius: 3, textAlign: "center" }}
        >
          <Stack spacing={3} alignItems="center">
            <Box
              component="img"
              src={logo}
              alt="Budget Trace"
              sx={{ width: 96, height: "auto" }}
            />
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Loading invitation...
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="background.default"
      >
        <Paper
          elevation={0}
          sx={{ width: 400, p: 4, borderRadius: 3 }}
        >
          <Stack spacing={3} alignItems="center">
            <Box
              component="img"
              src={logo}
              alt="Budget Trace"
              sx={{ width: 96, height: "auto" }}
            />
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={() => {
                window.location.href = "/";
              }}
              fullWidth
            >
              Go to Home
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (accepted) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="background.default"
      >
        <Paper
          elevation={0}
          sx={{ width: 400, p: 4, borderRadius: 3 }}
        >
          <Stack spacing={3} alignItems="center">
            <Box
              component="img"
              src={logo}
              alt="Budget Trace"
              sx={{ width: 96, height: "auto" }}
            />
            <Alert severity="success" sx={{ width: "100%" }}>
              Invitation accepted! Redirecting to workspace...
            </Alert>
            <CircularProgress size={24} />
          </Stack>
        </Paper>
      </Box>
    );
  }

  // If user is not logged in, show login page
  // Show loading if invitation is still being fetched
  if (!user) {
    if (loading) {
      return (
        <Box
          minHeight="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="background.default"
        >
          <Paper
            elevation={0}
            sx={{ width: 360, p: 4, borderRadius: 3, textAlign: "center" }}
          >
            <Stack spacing={3} alignItems="center">
              <Box
                component="img"
                src={logo}
                alt="Budget Trace"
                sx={{ width: 48, height: "auto" }}
              />
              <CircularProgress />
              <Typography variant="body1" color="text.secondary">
                Loading invitation...
              </Typography>
            </Stack>
          </Paper>
        </Box>
      );
    }

    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="background.default"
      >
        <Card sx={{ maxWidth: 400, width: "100%", m: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2.5} alignItems="center">
              {/* Workspace Logo */}
              {workspace?.logoUrl ? (
                <Box
                  component="img"
                  src={workspace.logoUrl}
                  alt={workspace.name || "Workspace"}
                  sx={{ 
                    width: 64, 
                    height: 64, 
                    borderRadius: 2,
                    objectFit: "cover",
                    boxShadow: 2
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 2
                  }}
                >
                  <Typography variant="h4" color="white" fontWeight="bold">
                    {(workspace?.name || "W")[0]?.toUpperCase()}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  You've been invited to join
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {workspace?.name || invitation?.workspaceId ? (workspace?.name || "Workspace") : "Loading..."}
                </Typography>
              </Box>
              
              {invitation?.email && (
                <Alert severity="info" sx={{ width: "100%", py: 0.5 }}>
                  <Typography variant="body2">
                    Please sign in with <strong>{invitation.email}</strong> to accept this invitation.
                  </Typography>
                </Alert>
              )}
              
              {!invitation?.email && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Loading invitation details...
                </Typography>
              )}
              
              <LoginPage 
                requiredEmail={invitation?.email || undefined}
                compact={true}
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // User is logged in, show acceptance UI
  // Show buttons even if workspace is not fully loaded (we have workspaceId from invitation)
  const displayWorkspaceName = workspace?.name || (invitation?.workspaceId ? "Workspace" : "Loading...");
  const userEmail = user?.email?.toLowerCase().trim();
  const invitationEmail = invitation?.email?.toLowerCase().trim();
  const emailMatches = invitationEmail && userEmail === invitationEmail;
  const canAccept = invitation && user && emailMatches;
  
  // If we have invitation but user email doesn't match, show warning and allow switching accounts
  if (invitation && user && !emailMatches) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="background.default"
      >
        <Paper
          elevation={0}
          sx={{ width: 400, p: 4, borderRadius: 3 }}
        >
          <Stack spacing={4} alignItems="center">
            {/* Workspace Logo */}
            {workspace?.logoUrl ? (
              <Box
                component="img"
                src={workspace.logoUrl}
                alt={workspace.name || "Workspace"}
                sx={{ 
                  width: 80, 
                  height: 80, 
                  borderRadius: 2,
                  objectFit: "cover",
                  boxShadow: 2
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: 2
                }}
              >
                <Typography variant="h3" color="white" fontWeight="bold">
                  {(displayWorkspaceName || "W")[0]?.toUpperCase()}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                You've been invited to join
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {displayWorkspaceName}
              </Typography>
            </Box>
            <Alert severity="warning" sx={{ width: "100%" }}>
              <Typography variant="body2">
                This invitation was sent to <strong>{invitation.email}</strong>, but you're signed in as <strong>{user.email}</strong>.
              </Typography>
            </Alert>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={async () => {
                // Sign out and show login page again
                await signOut(auth);
                // The component will re-render and show login page
              }}
            >
              Sign Out and Sign In with {invitation.email}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => window.location.href = "/"}
            >
              Go to Home
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }
  
  // Show buttons if invitation and workspace are loaded (even if workspace name is just "Workspace")
  if (!invitation) {
    // This shouldn't happen since we check loading above, but just in case - show error
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="background.default"
      >
        <Paper
          elevation={0}
          sx={{ width: 400, p: 4, borderRadius: 3 }}
        >
          <Alert severity="error">
            Invitation data is missing. Please try refreshing the page.
          </Alert>
        </Paper>
      </Box>
    );
  }
  
  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="background.default"
    >
      <Paper
        elevation={0}
        sx={{ width: 400, p: 4, borderRadius: 3 }}
      >
        <Stack spacing={4} alignItems="center">
          {/* Workspace Logo */}
          {workspace?.logoUrl ? (
            <Box
              component="img"
              src={workspace.logoUrl}
              alt={workspace.name || "Workspace"}
              sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: 2,
                objectFit: "cover",
                boxShadow: 2
              }}
            />
          ) : (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: 2
              }}
            >
              <Typography variant="h3" color="white" fontWeight="bold">
                {(displayWorkspaceName || "W")[0]?.toUpperCase()}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              You've been invited to join
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {displayWorkspaceName}
            </Typography>
          </Box>
          {invitation.role && (
            <Chip 
              label={`Role: ${invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}`}
              color="primary"
              variant="outlined"
            />
          )}
          <Stack direction="row" spacing={2} sx={{ width: "100%", mt: 2 }}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => {
                console.log(`🖱️  [CLICK] Decline button clicked`);
                handleDeclineInvitation();
              }}
              disabled={accepting}
              color="inherit"
            >
              Decline
            </Button>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleAcceptInvitation}
              disabled={accepting || !canAccept}
            >
              {accepting ? "Accepting..." : "Accept Invitation"}
            </Button>
          </Stack>
          {!canAccept && user && invitation && (
            <Alert severity="warning" sx={{ width: "100%" }}>
              <Typography variant="body2">
                Please sign in with <strong>{invitation.email}</strong> to accept this invitation.
              </Typography>
            </Alert>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}

export default InviteAcceptance;
